import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { map, tap } from 'rxjs';
import { environment } from '@env';
import { ApiService } from './api.service';
import { SecureCookieService } from './secure-cookie.service';
import { API_ENDPOINTS } from '../constants/api-endpoints.constants';
import { APP_ROUTES } from '../constants/routes.constants';
import {
  AdminAuthData,
  BuilderAuthData,
  BuilderInfo,
  BuilderProfileData,
  AdminLoginRequest,
  AuthResponse,
  BuilderSignupRequest,
  EditProfileRequest,
  ForgotPasswordRequest,
  IdTokenClaims,
  LoginRequest,
  ResetPasswordRequest,
  User,
  VerifyEmailRequest,
} from '../models/auth.models';

/** Cookie key constants — actual stored names are HMAC-hashed by SecureCookieService */
const COOKIE = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  ID_TOKEN: 'id_token',
  USER: 'current_user',
} as const;

/** Days until persistent login cookie expires */
const PERSIST_DAYS = 30;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly cookies = inject(SecureCookieService);
  private readonly router = inject(Router);

  // ── State ──────────────────────────────────────────────
  private readonly _currentUser = signal<User | null>(this.loadStoredUser());

  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);
  readonly isAdmin = computed(() => this._currentUser()?.role === 'admin');
  readonly isBuilder = computed(() => this._currentUser()?.role === 'builder');

  // ── Auth endpoints ──────────────────────────────────────
  login(payload: LoginRequest) {
    return this.api
      .post<AuthResponse>(API_ENDPOINTS.auth.login, payload)
      .pipe(tap((res) => this.handleAuthSuccess(res.data, payload.rememberMe)));
  }

  builderSignup(payload: BuilderSignupRequest) {
    return this.api.post<void>(API_ENDPOINTS.auth.builderSignup, payload);
  }

  verifyEmail(payload: VerifyEmailRequest) {
    return this.api.post<BuilderInfo>(API_ENDPOINTS.auth.verifyEmail, payload);
  }

  getBuilderUserDetails(userId: string) {
    return this.api.get<BuilderProfileData>(API_ENDPOINTS.auth.builderGetUserDetails(userId));
  }

  editBuilderUserDetails(userId: string, payload: EditProfileRequest) {
    return this.api.post<BuilderProfileData>(API_ENDPOINTS.auth.editUserDetails(userId), payload);
  }

  deleteBuilderAccount() {
    return this.api.delete<void>(API_ENDPOINTS.auth.deleteAccount);
  }

  patchCurrentUser(patch: Partial<Pick<User, 'fullName' | 'companyName' | 'address' | 'trade' | 'phoneNumber'>>): void {
    const user = this._currentUser();
    if (!user) return;
    this._currentUser.set({ ...user, ...patch });
  }

  resendOtp(email: string) {
    return this.api.post<void>(API_ENDPOINTS.auth.resendOtp, { email });
  }

  forgotPassword(payload: ForgotPasswordRequest) {
    return this.api.post<void>(API_ENDPOINTS.auth.forgotPassword, payload);
  }

  resetPassword(payload: ResetPasswordRequest) {
    return this.api.post<void>(API_ENDPOINTS.auth.resetPassword, payload);
  }

  adminLogin(payload: AdminLoginRequest, rememberMe = false) {
    return this.api
      .postTo<AdminAuthData>(environment.adminApiUrl, API_ENDPOINTS.auth.adminSignin, payload)
      .pipe(
        map((res) => res.data),
        tap((data) => this.handleAdminAuthSuccess(data, rememberMe)),
      );
  }

  builderLogin(payload: LoginRequest) {
    return this.api
      .post<BuilderAuthData>(API_ENDPOINTS.auth.builderSignin, payload)
      .pipe(tap((res) => this.handleBuilderAuthSuccess(res.data, payload.rememberMe ?? false)));
  }

  logout(redirectTo?: string): void {
    const destination =
      redirectTo ?? (this._currentUser()?.role === 'admin' ? APP_ROUTES.auth.adminLogin : APP_ROUTES.auth.login);

    Object.values(COOKIE).forEach((name) => this.cookies.delete(name));
    this._currentUser.set(null);
    this.router.navigateByUrl(destination);
  }

  /** Read the raw access token for use in the HTTP interceptor */
  getAccessToken(): string | null {
    return this.cookies.get(COOKIE.ACCESS_TOKEN);
  }

  // ── Private helpers ─────────────────────────────────────
  private handleAdminAuthSuccess(data: AdminAuthData, persist: boolean): void {
    const expiry = persist ? PERSIST_DAYS : undefined;
    const claims = this.decodeJwtPayload<IdTokenClaims>(data.id_token);

    const user: User = {
      id: claims?.sub ?? '',
      email: claims?.email ?? '',
      fullName: claims?.email?.split('@')[0] ?? '',
      role: 'admin',
      createdAt: new Date().toISOString(),
    };

    this.cookies.set(COOKIE.ACCESS_TOKEN, data.access_token, { expires: expiry });
    this.cookies.set(COOKIE.REFRESH_TOKEN, data.refresh_token, { expires: expiry });
    this.cookies.set(COOKIE.ID_TOKEN, data.id_token, { expires: expiry });
    this.cookies.set(COOKIE.USER, user, { expires: expiry });

    this._currentUser.set(user);
  }

  private handleBuilderAuthSuccess(data: BuilderAuthData, persist: boolean): void {
    const expiry = persist ? PERSIST_DAYS : undefined;
    const claims = this.decodeJwtPayload<IdTokenClaims>(data.id_token);
    const info = data.builder_info;
    const first = info.firstname?.trim() ?? '';
    const last = info.lastname?.trim() ?? '';
    const displayName = [first, last].filter(Boolean).join(' ');

    const user: User = {
      id: String(data.builder_id ?? info.builder_id ?? claims?.sub ?? ''),
      email: data.email || info.email || claims?.email || '',
      fullName: displayName || claims?.email?.split('@')[0] || '',
      role: 'builder',
      companyName: info.company_name ?? info.compnay_name ?? undefined,
      phoneNumber: info.phone_number ?? undefined,
      address: info.address ?? undefined,
      trade: info.trade ?? undefined,
      createdAt: new Date().toISOString(),
    };

    this.cookies.set(COOKIE.ACCESS_TOKEN, data.access_token, { expires: expiry });
    this.cookies.set(COOKIE.REFRESH_TOKEN, data.refresh_token, { expires: expiry });
    this.cookies.set(COOKIE.ID_TOKEN, data.id_token, { expires: expiry });
    this.cookies.set(COOKIE.USER, user, { expires: expiry });

    this._currentUser.set(user);
  }

  /** Decode the payload of a JWT without signature verification (server already validated). */
  private decodeJwtPayload<T>(token: string): T | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(base64);
      return JSON.parse(json) as T;
    } catch {
      return null;
    }
  }

  private handleAuthSuccess(auth: AuthResponse, persist = false): void {
    const expiry = persist ? PERSIST_DAYS : undefined; // undefined = session cookie

    this.cookies.set(COOKIE.ACCESS_TOKEN, auth.accessToken, { expires: expiry });
    this.cookies.set(COOKIE.REFRESH_TOKEN, auth.refreshToken, { expires: expiry });
    this.cookies.set(COOKIE.USER, auth.user, { expires: expiry });

    this._currentUser.set(auth.user);
  }

  private loadStoredUser(): User | null {
    return this.cookies.getObject<User>(COOKIE.USER);
  }
}
